import { Box, Container, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { useDarkMode, useMediaUp } from '../../../hooks';
import {
  arc,
  arcGISMob,
  autoCadMob,
  cad,
  google,
  googleMob,
  layer,
  open,
  oPenLayerMob,
  openstreetmapMob,
  post,
  postGISMob,
  qgis,
  qGISMob,
} from '../../../images/gpm';
import Image from 'next/image';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { useEffect, useState } from 'react';

const listImage = [
  {
    img: qgis,
    imgWidth: 128,
    imgHeight: 39,
    mobileImage: qGISMob,
    title: 'QGIS',
    caption: 'Native compatibility for flexible, open-source spatial analysis.',
  },
  {
    img: post,
    imgWidth: 140,
    imgHeight: 50,
    mobileImage: postGISMob,
    title: 'PostGIS',
    caption: 'Adds geospatial capabilities to your relational databases.',
  },
  {
    img: open,
    imgWidth: 168,
    imgHeight: 56,
    mobileImage: openstreetmapMob,
    title: 'OpenStreetMap',
    caption: 'Global open-source mapping for real-time reference and visualization.',
  },
  {
    img: layer,
    imgWidth: 165,
    imgHeight: 28,
    mobileImage: oPenLayerMob,
    title: 'OpenLayers',
    caption: 'High-performance browser-based maps embedded in GPM UI.',
  },
  {
    img: cad,
    imgWidth: 112,
    imgHeight: 36,
    mobileImage: autoCadMob,
    title: 'AutoCAD',
    caption: 'Synchronized CAD layers for precision-based project designs.',
  },
  {
    img: arc,
    imgWidth: 123,
    imgHeight: 38,
    mobileImage: arcGISMob,
    title: 'ArcGIS',
    caption: 'Direct integration for advanced spatial data and industry-grade GIS workflows.',
  },
  {
    img: google,
    imgWidth: 37,
    imgHeight: 37,
    mobileImage: googleMob,
    title: 'Google Earth Pro',
    caption: 'Terrain, satellite, and topographic overlays for enhanced spatial awareness.',
  },
];
const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginBottom: 90,
    [theme.breakpoints.up('md')]: {
      marginBottom: 180,
    },
  },
  title: {
    color: (isDarkMode) => (isDarkMode ? theme.palette.common.white : theme.palette.common.black),
    fontSize: 24,
    fontWeight: 700,
    textAlign: 'center',
  },
  imageContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '10px',
  },
  imageWrapper: {
    position: 'relative',
    textAlign: 'center',
    cursor: 'pointer',
    minHeight: 165,
    backgroundColor: theme.palette.common.white,
    '&:hover $caption': {
      opacity: 1,
      transform: 'translateY(0)',
    },
    [theme.breakpoints.up('sm')]: {
      minHeight: 'auto',
      backgroundColor: 'transparent',
    },
  },
  caption: {
    position: 'absolute',
    bottom: -90,
    left: -30,
    width: 200,
    height: 84,
    backgroundColor: '#FCFCFC',
    color: theme.palette.common.black,
    fontSize: 10,
    border: '1px solid #116666',
    opacity: 0,
    transform: 'translateY(10px)',
    transition: 'all 0.3s ease',
    borderRadius: 10,
    padding: theme.spacing(1),
    zIndex: 999,
  },
  titlePopup: {
    color: theme.palette.common.black,
    fontSize: 20,
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
}));

const TechnologiesBlock = () => {
  const { isDarkMode } = useDarkMode();
  const classes = useStyles(isDarkMode);
  const isSmallUp = useMediaUp('sm');

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => {
      setMounted(false); // allows cancelling internal effects if needed
    };
  }, []);

  if (!mounted) return null;

  return (
    <Container className={classes.container} maxWidth={false} fixed>
      <Grid justifyContent="center" container spacing={isSmallUp ? 8 : 4}>
        <Grid xs={12} item>
          {isSmallUp && <Typography className={classes.title}>{'Integrated Technologies within GPM:'}</Typography>}
        </Grid>
        <Grid xs={12} item>
          {isSmallUp ? (
            <Box className={classes.imageContainer} display={'flex'} alignItems="center" justifyContent="center">
              {listImage.map((image) => (
                <Box key={image.img} className={classes.imageWrapper}>
                  <Image alt={'icon'} key={image.img} src={image.img} height={50} width={140} />
                  <Box className={classes.caption}>
                    <Typography className={classes.titlePopup}>{image.title}</Typography>
                    {image.caption}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Carousel
              autoPlay
              infiniteLoop
              showThumbs={false}
              showIndicators={true}
              renderIndicator={(onClickHandler, isSelected, index, label) => {
                const dotStyle = {
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  margin: '0 6px',
                  borderRadius: '50%',
                  backgroundColor: '#0061E5',
                  opacity: isSelected ? '100%' : '20%',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                };

                return (
                  <span
                    key={index}
                    style={dotStyle}
                    onClick={onClickHandler}
                    onKeyDown={onClickHandler}
                    role="button"
                    tabIndex={0}
                    aria-label={`${label} ${index + 1}`}
                  />
                );
              }}
            >
              {listImage.map((item) => (
                <Box
                  padding={'12px 10px'}
                  key={item.img}
                  className={classes.imageWrapper}
                  border={'1px solid #116666'}
                  borderRadius={12}
                >
                  <Image
                    alt={'icon'}
                    key={item.img}
                    src={isSmallUp ? item.img : item.mobileImage}
                    height={isSmallUp ? 50 : item.imgHeight}
                    width={isSmallUp ? 140 : item.imgWidth}
                  />
                  <Box mt={2} fontSize={12} color={'black'}>
                    <Typography className={classes.titlePopup}>{item.title}</Typography>
                    {item.caption}
                  </Box>
                </Box>
              ))}
            </Carousel>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default TechnologiesBlock;
